import React, { useState, useEffect, useCallback } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Button from 'react-bootstrap/Button';
import { format } from 'date-fns';

import {confirm} from '../../Confirmation';
import ErrorCatcher from '../ErrorCatcher';
import Users from '../../../api/Users';


/** This component can be used to either check a user in or view their latest checkins.   
 * 
 * @param {object} userInfo - the user object, such as that saved when you login or when you look up someone's profile. Should at least have 'first_name', 'last_name', and 'id'
 * @param {string} buttonText - the text to display on the button, otherwise will be "Check In This User"
 * @param {boolean} hideCheckins - if true, will not show the last checkins, defaults to 'false'
 * @param {boolean} hideButton - if true, will not show the checkin button, defaults to 'false
 * @param {number} checkinAmount - the number of checkins to display, defaults too '1'
 * @param {string} variant - the variant of the button to display, defaults to 'primary'
 * @param {string} classes - any additional classes to add to the button, defaults to ""
*/
export const CheckinButton = ({
    userInfo=null,
    buttonText="Check In This User",
    hideCheckins=false,
    hideButton=false,
    checkinAmount=1,
    variant="primary",
    classes=""
}) => {

    const [error, setError]=useState();
    const [ lastCheckins, setLastCheckins ] = useState([]);
    
    useEffect(() => {
        if (userInfo?.id) {
            loadUserLastCheckin(userInfo.id);
        }
    },[userInfo]);

    useEffect(() => {
        return () => {
            setLastCheckins(null);
        }
    },[]);

    const loadUserLastCheckin = async (user_id) => {
        await Users.Checkin.get({
            user_id: user_id,
            sort_dir: "DESC",
            limit: checkinAmount,
        }).then(async response => {
            if (!response.errors) {
                console.log(response.data)
                setLastCheckins(response.data?.map(checkin =>new Date(checkin.checkin_at)));
            } else if(response.errors) {
                console.error(response.errors);
                setError(<ErrorCatcher title="Cannot load last check in" error={response.errors} />)
            }
        }).catch(e => console.error(e));
    }

    const confirmHandler = useCallback((props) => {
        confirm(`Check in ${props.text || "user"} to the facility?`, {
            title: props.title,
            okText:"Yes",
            cancelText:"No",
            cancelButtonStyle:"light"
        }).then(result =>{
            if (result===true) props.click();
        }).catch(e => console.error(e));
    },[]);

    const checkinUser = async (user_id) => {
        await Users.Checkin.create({
            user_id: user_id,
        }).then(async response => {
            if (!response.errors && response.data) {
                setLastCheckins(prev => [...prev, (new Date(response.data?.checkin_at))]);
            } else {
                console.error(response.errors);
                setError(<ErrorCatcher title="Cannot check in user" error={response.errors} />)
            }
        }).catch(e => console.error(e));
    }

    if (!userInfo) return (
        <div className="text-center">
            <SkeletonTheme color="#e0e0e0">
                <Skeleton height={12} count={1} />
            </SkeletonTheme>
        </div>
    );
    
    return (
        userInfo &&
            <div className="site-col" style={{"alignItems": "center"}}>
                {!hideButton &&
                    <Button variant={variant} className={`btn btn-checkin ${classes}`} data-cy="checkin-btn"
                        onClick={() => {
                            confirmHandler({
                                title: "Confirm Check In",
                                text:`${userInfo.first_name} ${userInfo.last_name}`,
                                click:() => checkinUser(userInfo.id)
                            });
                        }}
                    >
                        {buttonText}
                    </Button>
                }
                {!hideCheckins &&
                    <div className="checked-in-today text-center mt-1">
                        {lastCheckins?.length ?
                            <>
                                <span>
                                    {lastCheckins.length === 1 ? 
                                        "Last check in:" 
                                    : 
                                        `Last ${checkinAmount} check ins:`
                                    }
                                    
                                </span><br />
                                {lastCheckins?.map((checkin)=>(
                                    <>
                                        <span data-cy="checkin-last">{format(checkin, "M/d/Y h:mm b")}</span>
                                        <br />
                                    </>

                                ))}
                            </>
                        :
                            <>No prior check in.</>
                        }
                    </div>
                }
                {error}
            </div>
    );
}